/**
 * Comments in a `.zwdoc` body (#251): a `comment` mark on the commented
 * text, or a zero-width `comment_anchor` node for a point comment. Threads
 * live in `header.comments`; these helpers only deal with the body.
 */

import type { Node as PMNode } from "prosemirror-model";
import { EditorState, type Transaction } from "prosemirror-state";
import { zwdocSchema } from "./schema";
import type {
  ZwDocCommentMessage,
  ZwDocCommentThread,
} from "./generated/zwDoc";

export type CommentMessage = ZwDocCommentMessage;
export type CommentThread = ZwDocCommentThread;
/** `header.comments`: threads keyed by comment id. */
export type CommentStore = Record<string, CommentThread>;

/** Where a comment sits in the document (ProseMirror positions). */
export type ZwDocCommentRange = {
  id: string;
  /** First commented position (the anchor node for a point comment). */
  from: number;
  /** End of the commented text (`from + 1` for a point comment). */
  to: number;
  point: boolean;
  /** The commented text, trimmed, for the margin card. */
  text: string;
};

/**
 * Every comment in document order. A mark split by other formatting (or
 * spread over several blocks) reports one range from its first to its last
 * character.
 */
export function zwDocCommentRanges(doc: PMNode): ZwDocCommentRange[] {
  const byId = new Map<string, { from: number; to: number; point: boolean }>();
  doc.descendants((node, pos) => {
    if (node.type.name === "comment_anchor") {
      const id = String(node.attrs.id);
      if (id !== "" && byId.has(id) === false) {
        byId.set(id, { from: pos, to: pos + node.nodeSize, point: true });
      }
      return false;
    }
    if (node.isInline === false) return true;
    for (const mark of node.marks) {
      if (mark.type.name !== "comment") continue;
      const id = String(mark.attrs.id);
      if (id === "") continue;
      const end = pos + node.nodeSize;
      const seen = byId.get(id);
      if (typeof seen === "undefined" || seen.point) {
        byId.set(id, { from: pos, to: end, point: false });
      } else {
        seen.from = Math.min(seen.from, pos);
        seen.to = Math.max(seen.to, end);
      }
    }
    return false;
  });
  return [...byId.entries()]
    .map(([id, range]) => ({
      id,
      ...range,
      text: range.point
        ? ""
        : doc
            .textBetween(range.from, range.to, " ", " ")
            .replace(/\s+/g, " ")
            .trim(),
    }))
    .sort((a, b) => (a.from !== b.from ? a.from - b.from : a.to - b.to));
}

/** Steps that remove comment `id`'s marks and anchor nodes from `tr`'s doc. */
export function removeCommentSteps(tr: Transaction, id: string): Transaction {
  const mark = zwdocSchema.marks.comment.create({ id });
  const anchors: number[] = [];
  tr.doc.descendants((node, pos) => {
    if (node.type.name === "comment_anchor" && node.attrs.id === id) {
      anchors.push(pos);
    }
    return node.isInline === false;
  });
  tr.removeMark(0, tr.doc.content.size, mark);
  // Delete back to front so earlier positions stay valid.
  for (const pos of anchors.reverse()) tr.delete(pos, pos + 1);
  return tr;
}

/** `doc` without comment `id` (marks and anchors). */
export function withoutComment(doc: PMNode, id: string): PMNode {
  return removeCommentSteps(EditorState.create({ doc }).tr, id).doc;
}

/** `doc` with every comment mark and anchor removed (export). */
export function withoutAllComments(doc: PMNode): PMNode {
  const tr = EditorState.create({ doc }).tr;
  tr.removeMark(0, doc.content.size, zwdocSchema.marks.comment);
  const anchors: number[] = [];
  tr.doc.descendants((node, pos) => {
    if (node.type.name === "comment_anchor") anchors.push(pos);
    return node.isInline === false;
  });
  for (const pos of anchors.reverse()) tr.delete(pos, pos + 1);
  return tr.doc;
}
